// ============================================================
//  ChatsApp.js — "CommandKit Chats", the installable chat-only app (/chats)
//
//  Just the website-chat inbox: conversations + replies, Online/Away,
//  sound, push notifications (even when the app is closed, with reply from
//  the notification) and an unread count on the app icon.
//  Install it from the browser (Chrome/Edge: the install button here or in
//  the address bar; iPhone: Share → Add to Home Screen) and it opens in
//  its own window like a desktop app.
// ============================================================
import './ChatsApp.css';
import '../website-widget/WebsiteWidgetPage.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getWidgets, liveChatPresence } from '../../api/client';
import LiveChatInbox from '../website-widget/LiveChatInbox';
import useLiveChatPulse, { lcSoundOn, setLcSound, playChime } from '../../components/layout/useLiveChatPulse';
import PushBell from '../../components/layout/PushBell';

const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

/** Point the page at the chat app's own manifest + register its service worker (only on /chats). */
function useChatAppShell() {
  useEffect(() => {
    let link = document.querySelector('link[rel="manifest"]');
    const prevHref = link?.getAttribute('href');
    if (!link) { link = document.createElement('link'); link.rel = 'manifest'; document.head.appendChild(link); }
    link.setAttribute('href', '/chats.webmanifest');

    let theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.getAttribute('content');
    if (!theme) { theme = document.createElement('meta'); theme.name = 'theme-color'; document.head.appendChild(theme); }
    theme.setAttribute('content', '#07150E');

    let apple = document.querySelector('link[rel="apple-touch-icon"]');
    const prevApple = apple?.getAttribute('href');
    if (!apple) { apple = document.createElement('link'); apple.rel = 'apple-touch-icon'; document.head.appendChild(apple); }
    apple.setAttribute('href', '/chats-icon-192.png');

    const prevTitle = document.title;
    document.title = 'CommandKit Chats';

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/chats-sw.js', { scope: '/chats' }).catch(() => { /* app still works without it */ });
    }
    // A notification clicked while the app was in the background → open that chat
    const onMsg = e => { if (e.data?.type === 'ck-lc-open') window.dispatchEvent(new CustomEvent('ck:lc-open', { detail: { chatId: e.data.chatId, reply: !!e.data.reply } })); };
    navigator.serviceWorker?.addEventListener('message', onMsg);

    return () => {
      if (prevHref) link.setAttribute('href', prevHref); else link.remove();
      if (prevTheme) theme.setAttribute('content', prevTheme);
      if (prevApple) apple.setAttribute('href', prevApple);
      document.title = prevTitle;
      navigator.serviceWorker?.removeEventListener('message', onMsg);
    };
  }, []);
}

export default function ChatsApp() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const initialChat = useMemo(() => Number(new URLSearchParams(location.search).get('c')) || null, [location.search]);
  const focusReply  = useMemo(() => new URLSearchParams(location.search).get('reply') === '1', [location.search]);
  const [widgets, setWidgets]   = useState(null);
  const [presence, setPresence] = useState(null);
  const [sound, setSound]       = useState(lcSoundOn);
  const [installEvt, setInstallEvt] = useState(null);
  const [installed, setInstalled]   = useState(isStandalone);
  const [iosHint, setIosHint]   = useState(false);

  useChatAppShell();
  const { unread } = useLiveChatPulse({ notify: true, badge: true });

  const load = useCallback(async () => {
    try {
      const res = await getWidgets();
      setWidgets(res.data.widgets || []);
      setPresence(res.data.presence || null);
    } catch { setWidgets([]); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  useEffect(() => {
    const onPrompt = e => { e.preventDefault(); setInstallEvt(e); };
    const onInstalled = () => { setInstalled(true); setInstallEvt(null); toast.success('CommandKit Chats is installed'); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
  }, []);

  async function install() {
    if (installEvt) {
      installEvt.prompt();
      const choice = await installEvt.userChoice.catch(() => null);
      if (choice?.outcome === 'accepted') setInstalled(true);
      setInstallEvt(null);
    } else if (isIOS()) {
      setIosHint(true);
    } else {
      toast('Use your browser menu → “Install CommandKit Chats” (Chrome / Edge).', { icon: '💡', duration: 6000 });
    }
  }

  async function setStatus(status) {
    try { const res = await liveChatPresence(status); setPresence(res.data.presence); }
    catch { toast.error('Could not change your status'); }
  }

  function toggleSound() {
    const next = !sound; setSound(next); setLcSound(next); if (next) playChime();
  }

  if (loading) return <div className="chapp-center"><span className="wwg-spin" /></div>;
  if (!user) {
    return (
      <div className="chapp-center">
        <div className="chapp-card">
          <img src="/chats-icon-192.png" alt="" width="64" height="64" />
          <h1>CommandKit Chats</h1>
          <p>Sign in to your CommandKit account to answer your website visitors.</p>
          <Link className="wwg-btn wwg-btn-dark" to="/login">Sign in</Link>
          <span>After signing in, come back to this app.</span>
        </div>
      </div>
    );
  }

  const away = presence?.status === 'away';
  return (
    <div className="chapp wwg-vars">
      <header className="chapp-bar">
        <div className="chapp-brand">
          <img src="/chats-icon-192.png" alt="" width="30" height="30" />
          <b>Chats</b>
          {unread > 0 && <span className="chapp-count" aria-label={`${unread} waiting`}>{unread > 99 ? '99+' : unread}</span>}
        </div>
        <div className="chapp-actions">
          <div className="chapp-presence" role="radiogroup" aria-label="Your status">
            <button type="button" role="radio" aria-checked={!away} className={!away ? 'is-on' : ''} onClick={() => away && setStatus('online')}><i className="dot is-green" /> <span>Online</span></button>
            <button type="button" role="radio" aria-checked={away} className={away ? 'is-on' : ''} onClick={() => !away && setStatus('away')}><i className="dot" /> <span>Away</span></button>
          </div>
          <button type="button" className={`chapp-icon ${sound ? 'is-on' : ''}`} onClick={toggleSound} aria-pressed={sound}
            aria-label={sound ? 'Mute new message sound' : 'Turn on new message sound'} title={sound ? 'Sound on' : 'Sound off'}>
            <i className={`fa-solid ${sound ? 'fa-volume-high' : 'fa-volume-xmark'}`} />
          </button>
          <PushBell tone="dark" />
          {!installed && (
            <button type="button" className="chapp-pill is-brand" onClick={install}><i className="fa-solid fa-download" /> <span>Install app</span></button>
          )}
          <a className="chapp-icon" href="/website-widget" target={installed ? '_blank' : undefined} rel="noopener noreferrer" title="Open the full dashboard" aria-label="Open the full dashboard">
            <i className="fa-solid fa-table-columns" />
          </a>
        </div>
      </header>

      {iosHint && (
        <div className="chapp-hint" role="status">
          <span>On iPhone/iPad: tap <b>Share</b> <i className="fa-solid fa-arrow-up-from-bracket" /> then <b>Add to Home Screen</b>.</span>
          <button type="button" onClick={() => setIosHint(false)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>
      )}

      <main className="chapp-main">
        {widgets === null ? (
          <div className="chapp-center"><span className="wwg-spin" /></div>
        ) : widgets.length === 0 ? (
          <div className="chapp-center">
            <div className="chapp-card">
              <h1>No website widget yet</h1>
              <p>Create a widget and put it on your website — then your visitors’ chats appear here.</p>
              <a className="wwg-btn wwg-btn-dark" href="/website-widget">Create a widget</a>
            </div>
          </div>
        ) : (
          <LiveChatInbox widgets={widgets} presence={presence} initialChat={initialChat} focusReply={focusReply} />
        )}
      </main>
    </div>
  );
}
